import axios from 'axios';

const getClientList = (signal = null) => {
  return new Promise((resolve, reject) => {
    axios.get(`/v1/clients?filters=topLevel`, { signal })
    .then(response => {
      resolve(response.data);
    })
    .catch(err => {
      if (axios.isCancel(err)) {
        return reject(err.message);
      }
      console.error(err);
      reject(err);
    });
  });
};

const getClient = (clientId, signal = null) => {
  return new Promise((resolve, reject) => {
    axios.get(`/v1/clients/${clientId}`, {signal})
    .then(response => {
      resolve(response.data);
    })
    .catch(err => {
      if (axios.isCancel(err)) {
        return reject(err.message);
      }
      console.error(err);
      reject(err);
    });
  });
};

const getClientByLegacyId = (clientId, signal = null) => {
  return new Promise((resolve, reject) => {
    axios.get(`/v1/clients/${clientId}?source=legacy`, { signal })
    .then(response => {
      resolve(response.data);
    })
    .catch(err => {
      if (axios.isCancel(err)) {
        return reject(err.message);
      }
      console.error(err);
      reject(err);
    });
  });
};

const getClientsOverview = async (signal = null) => {
  try {
    const response = await axios.get(`/v1/clients/overview`, {
      signal
    });
    if (response.status !== 200) {
      throw new Error('Error fetching clients:', response.statusText);
    }
    return response;
  } catch (err) {
    console.error(err);
  }
}


const getTopLevelClient = (childClientId, signal = null) => {
  return new Promise((resolve, reject) => {
    axios.get(`/v1/clients?familyOf=${childClientId}`, {signal})
    .then(response => {
      if (response.status !== 200 || response.data.length !== 1) {
        return reject('Unexpected response from server looking up parent location');
      }
      return resolve(response.data[0]);
    })
    .catch(err => {
      console.error(err);
      reject(err);
    });
  });
};

// NOTE: this method is dangerous because if the object passed is missing any
// of the following information: (name, primaryAddress, contacts, notes, deactivated, employeeIdentifier) these fields will
// null out the existing values. Right now only the Client Detail page is using this method and is providing
// the full client object so all information is retained.
// todo: create dynamic patch method only modify what is provided
const updateClient = (client, signal = null) => {
  return new Promise((resolve, reject) => {
    axios
    .put(`/v1/clients/${client._id}`, {
      name: client.name,
      primaryAddress: client.primaryAddress,
      contacts: client.contacts,
      notes: client.notes,
      deactivated: client.deactivated,
      employeeIdentifier: client.employeeIdentifier
    }, {signal})
    .then(response => {
      resolve();
    })
    .catch(err => {
      console.error(err);
      reject(err);
    });
  });
};

const patchClient = (clientId, clientChanges, signal = null) => {
  return new Promise((resolve, reject) => {
    if (!clientId) {
      return reject(new Error('Client id is required'));
    }
    if (!clientChanges || Object.keys(clientChanges).length === 0) {
      return reject(new Error('Patch body is required'));
    }
    axios
    .patch(`/v1/clients/${clientId}`, clientChanges, {signal})
    .then(res => {
      if (res.status !== 201) {
        return reject(new Error('Unexpected response from server updating client'));
      }
      resolve(res.data);
    })
    .catch(err => {
      console.error(err);
      reject(err);
    });
  })
};

const setNotes = (clientId, notes, signal = null) => {
  return new Promise((resolve, reject) => {
    axios
    .put(`/v1/clients/${clientId}/notes`, {notes}, {signal})
    .then(response => {
      resolve();
    })
    .catch(err => {
      console.error(err);
      reject(err);
    });
  });
};

const setServices = (clientId, services, signal = null) => {
  return new Promise((resolve, reject) => {
    axios
    .put(`/v1/clients/${clientId}/services`, {services}, {signal})
    .then(response => {
      resolve();
    })
    .catch(err => {
      console.error(err);
      reject(err);
    });
  });
};

const uploadLogo = (clientId, logo, signal = null) => {
  const data = new FormData();
  data.append('image', logo);
  return new Promise((resolve, reject) => {
    axios
    .post(`/v1/clients/${clientId}/logo`, data, {signal})
    .then(result => resolve(result.data))
    .catch(err => {
      console.error(err);
      reject(err);
    });
  });
};

const getNonImportedLegacyClientList = (signal = null) => {
  return new Promise((resolve, reject) => {
    axios.get('/v1/clients?source=legacy&filters=topLevel,nonImported', { signal })
    .then(response => {
      resolve(response.data);
    })
    .catch(err => {
      if (axios.isCancel(err)) {
        return reject(err.message);
      }
      console.error(err);
      reject(err);
    });
  });
};

const getClientSummary = (clientId, sites, isRandoms, signal = null) => {
  return new Promise((resolve, reject) => {
    axios.get(`/v1/clients/${clientId}/employees?summary=true&sites=${sites}${isRandoms ? '&isRandoms' : ''}`, { signal })
    .then(response => {
      resolve(response.data);
    })
    .catch(err => {
      if (axios.isCancel(err)) {
        return reject(err.message);
      }
      console.error(err);
      reject(err);
    });
  });
};

const getFastTestResultsForClient = (clientId, sites, startDate, endDate, signal = null) => {
  let url = `/v1/clients/${clientId}/fasttest?sites=${sites}`;
  if (!!startDate) {
    url += `&startDate=${startDate.format('YYYY-MM-DD')}T00:00:00`;
  }
  if (!!endDate) {
    url += `&endDate=${endDate.format('YYYY-MM-DD')}T23:59:59`;
  }
  return new Promise((resolve, reject) => {
    axios.get(url, { signal })
    .then(response => resolve(response.data))
    .catch(err => {
      if (axios.isCancel(err)) {
        return reject(err.message);
      }
      console.error(err);
      reject(err);
    });
  });
};

const getRandomsPoolsForClient = (clientId, signal = null) => {
  return new Promise((resolve, reject) => {
    axios.get(`/v1/clients/${clientId}/pools`, { signal })
    .then(response => {
      resolve(response.data);
    })
    .catch(err => {
      if (axios.isCancel(err)) {
        return reject(err.message);
      }
      console.error(err);
      reject(err);
    });
  });
};

/**
 * getRandomsPoolsByClient
 * @param {string} clientId
 * @param {Object} [summary]
 * @param {true|Object|String|Array<String> } summary - true for default summary fields or
 * choose the fields returned from RandomPools Schema 
 * using https://mongoosejs.com/docs/5.x/docs/api.html#query_Query-select.
 * @param {object} [signal=null] - the cancel signal sent to Axios for request cancellation (optional)
 * @returns RandomPools[]
 */
const getRandomsPoolsByClient = (clientId, summary, signal = null) => {
  const query = Array.isArray(summary)
    ? summary.map((item) => `summary=${encodeURIComponent(item)}`).join('&')
    : new URLSearchParams({ summary }).toString();

  return new Promise((resolve, reject) => {
    axios.get(`/v1/clients/${clientId}/pools?${query}`, { signal })
    .then(response => {
      resolve(response.data);
    })
    .catch(err => {
      if (axios.isCancel(err)) {
        return reject(err.message);
      }
      console.error(err);
      reject(err);
    });
  });
};

const getClientComplianceSummary = (clientId, signal = null) => {
  return new Promise((resolve, reject) => {
    axios.get(`/v1/clients/${clientId}/compliance`, { signal })
    .then(response => {
      resolve(response.data);
    })
    .catch(err => {
      if (axios.isCancel(err)) {
        return reject(err.message);
      }
      console.error(err);
      reject(err);
    });
  });
};

const getClientAudits = (clientId, signal = null) => {
  return new Promise((resolve, reject) => {
    axios.get(`/v1/clients/${clientId}/audits`, {signal})
    .then(response => {
      resolve(response.data);
    })
    .catch(err => {
      if (axios.isCancel(err)) {
        return reject(err.message);
      }
      console.error(err);
      reject(err);
    });
  });
};

const getActiveEmployeesBySite = (clientId, sites, signal = null) => {
  return new Promise((resolve, reject) => {
    axios.get(`/v1/clients/${clientId}/employees?active=true&sites=${sites}`, { signal }
    )
    .then(response => {
      resolve(response.data);
    })
    .catch(err => {
      if (axios.isCancel(err)) {
        return reject(err.message);
      }
      console.error(err);
      reject(err);
    });
  });
};

const getAlerts = (clientId, sites, alertTypes, signal = null) => {
  return new Promise((resolve, reject) => {
    axios.get(`/v1/clients/${clientId}/alerts?alertTypes=${alertTypes.join(',')}&sites=${sites}&summary&problemsAndWarningsOnly=true`, { signal })
    .then(response => {
      resolve(response.data);
    })
    .catch(err => {
      if (axios.isCancel(err)) {
        return reject(err.message);
      }
      console.error(err);
      reject(err);
    });
  });
};

const getDocReviewAlerts = (clientId, sites, signal = null, mvrOrEPN) => {
  return new Promise((resolve, reject) => {
    axios.get(`/v1/clients/${clientId}/alerts?alertTypes=${mvrOrEPN}&sites=${sites}`, { signal })
    .then(response => {
      resolve(mvrOrEPN === 'MVR' ? response.data.mvrAlerts : response.data.epnAlerts);
    })
    .catch(err => {
      if (axios.isCancel(err)) {
        return reject(err.message);
      }
      console.error(err);
      reject(err);
    });
  });
};

const getStaleApplicantAlerts = (clientId, sites, signal = null) => {
  return new Promise((resolve, reject) => {
    axios.get(`/v1/clients/${clientId}/alerts?alertTypes=staleApplicants&sites=${sites}`, { signal })
    .then(response => {
      resolve(response.data.staleApplicantAlerts);
    })
    .catch(err => {
      if (axios.isCancel(err)) {
        reject(err.message);
      }
      console.error(err);
      reject(err);
    });
  });
};

const getCSAAlerts = (clientId, sites, signal = null) => {
  return new Promise((resolve, reject) => {
    axios.get(`/v1/clients/${clientId}/alerts?alertTypes=CSA&sites=${sites}`, { signal })
    .then(response => {
      resolve(response.data);
    })
    .catch(err => {
      if (axios.isCancel(err)) {
        return reject(err.message);
      }
      console.error(err);
      reject(err);
    });
  });
};

const getDACAlerts = (clientId, sites, signal = null) => {
  return new Promise((resolve, reject) => {
    axios.get(`/v1/clients/${clientId}/alerts?alertTypes=DAC&sites=${sites}`, { signal })
    .then(response => {
      resolve(response.data.dacStatus);
    })
    .catch(err => {
      if (axios.isCancel(err)) {
        return reject(err.message);
      }
      console.error(err);
      reject(err);
    });
  });
};

const getDocumentAlerts = (clientId, sites, signal = null) => {
  return new Promise((resolve, reject) => {
    axios.get(`/v1/clients/${clientId}/alerts?alertTypes=documents&sites=${sites}`, { signal })
    .then(response => {
      resolve(response.data);
    })
    .catch(err => {
      if (axios.isCancel(err)) {
        return reject(err.message);
      }
      console.error(err);
      reject(err);
    });
  });
};

const getMissingDocuments = (clientId, sites, allEmployees, signal = null) => {
  return new Promise((resolve, reject) => {
    axios.get(`/v1/clients/${clientId}/alerts?alertTypes=missingDocuments&sites=${sites}&allEmployees=${allEmployees}`, { signal })
    .then(response => {
      resolve(response.data.docAlerts);
    })
    .catch(err => {
      if (axios.isCancel(err)) {
        return reject(err.message);
      }
      console.error(err);
      reject(err);
    });
  });
};

const getMissingAndExpiredDocuments = (
    clientId,
    sites,
    applicant,
    employee,
    inactive,
    terminated,
    missing,
    expired,
    expiredDays,
    expiring,
    expiringDays,
    signal = null
) => {
  const employmentStatuses = [];
  if (applicant) {
    employmentStatuses.push('Applicant');
  }
  if (employee) {
    employmentStatuses.push('Employee');
  }
  if (inactive) {
    employmentStatuses.push('Inactive');
  }
  if (terminated) {
    employmentStatuses.push('Terminated');
  }
  const docStatuses = [];
  if (missing) {
    docStatuses.push('missing');
  }
  if (expired) {
    docStatuses.push('expired');
  }
  if (expiring) {
    docStatuses.push('expiring');
  }
  return new Promise((resolve, reject) => {
        axios.get(
            // We need to pass allEmployees=true so that it gets them all from the sproc; then we also pass the list of
            // statuses that we really care about, so that they are filtered from the sproc results at the server.
            // Also pass the list of docSTatuses that we want, as well as expiredDays and expiringDays
            // -- and let the server do all the filtering.
            `/v1/clients/${clientId}/alerts?alertTypes=missingDocuments&sites=${sites}&allEmployees=true&employmentStatuses=${employmentStatuses}&docStatuses=${docStatuses}&expiredDays=${expiredDays}&expiringDays=${expiringDays}`,
            { signal }
        )
        .then(response => {
          resolve(response.data.docAlerts);
        })
        .catch(err => {
          if (axios.isCancel(err)) {
            return reject(err.message);
          }
          console.error(err);
          reject(err);
        });
      }
  );
};

const getCSAReportData = (clientId, forUser, sites, reportType, signal = null) => {
  return new Promise((resolve, reject) => {
    let url = `/v1/clients/${clientId}/csa?forUser=${forUser}&reportType=${reportType}`;
    if (reportType === 'csaLocation') {
      url += `&selectedClientId=${sites}`
    } else {
      url += `&selectedDOTIds=${sites.join(',')}`;
    }
    axios.get(url, { signal })
    .then(response => {
      const results = response.data.results || [];
      resolve(results);
    })
    .catch(err => {
      if (axios.isCancel(err)) {
        return reject(err.message);
      }
      console.error(err);
      reject(err);
    });
  });
};

// this is misleading: 'sites' is either sites or dotNums?
const getCrashReportData = (clientId, forUser, sites, reportType, signal = null) => {
  return new Promise((resolve, reject) => {
    let url = `/v1/clients/${clientId}/crashes?forUser=${forUser}&reportType=${reportType}`;
    if (reportType === 'crashLocation') {
      url += `&selectedClientId=${sites}`
    } else {
      url += `&selectedDOTIds=${sites.join(',')}`;
    }
    axios.get(url, { signal })
    .then(response => {
      const results = response.data.results || [];
      resolve(results);
    })
    .catch(err => {
      if (axios.isCancel(err)) {
        return reject(err.message);
      }
      console.error(err);
      reject(err);
    });
  });
};

/**
 * Get Med Cards for a single client location or include child locations
 * @param {string} legacyClientId - The legacy ID of the client
 * @param {Array<string>} [selectedSites=null] - An array of selected site IDs (optional)
 * @param {object} [signal=null] - the cancel signal sent to Axios for request cancellation (optional)
 * @returns {Promise<Array<object>>} - The response data from the API
 * @property {string} response[].companyName - The name of the company associated with the med card
 * @property {string} response[].locationName - The name of the location associated with the med card
 * @property {string} response[].employeeName - The name of the employee holding the med card
 * @property {string} response[].ssn - The Social Security Number of the employee
 * @property {string} response[].documentBucketName - The type of document (Medical Examiners Certificate)
 * @property {string} response[].docDate - The date the document was issued, formatted as an ISO 8601 string
 * @property {string} response[].expirationDate - The expiration date of the document, formatted as an ISO 8601 string
 */
const getReportOfAllMedCardsByLocation = async ({clientId, isIncludeChildren, signal = null}) => {
  try {
    const url = `/v1/clients/medCards`;
    const requestBody = {
      clientId,
      isIncludeChildren,
    };
    const response = await axios.post(url, requestBody, { signal });
    return response.data;
  } catch (err) {
    console.error('Error fetching med cards:', err);
    throw err;
  }
};

const getEmployeesDOTReportData = (clientId, forUser, sites, reportType, signal = null) => {
  return new Promise((resolve, reject) => {
    let url = `/v1/clients/${clientId}/employeesByDot?reportType=employeesDOT`;
    url += `&selectedDOTIds=${sites.join(',')}`;
    axios.get(url, { signal })
    .then(response => {
      const results = response.data || [];
      resolve(results);
    })
    .catch(err => {
      if (axios.isCancel(err)) {
        return reject(err.message);
      }
      console.error(err);
      reject(err);
    });
  });
};

const getAccidentReportData = (clientId, forUser, sites, reportType, signal = null) => {
  return new Promise((resolve, reject) => {
    let url = `/v1/clients/${clientId}/accidents?forUser=${forUser}&reportType=${reportType}`;
    if (reportType === 'accidentLocation') {
      url += `&selectedClientId=${sites}`
    } else {
      url += `&selectedDOTIds=${sites.join(',')}`;
    }
    axios.get(url, { signal })
    .then(response => {
      const results = response.data.results || [];
      resolve(results);
    })
    .catch(err => {
      if (axios.isCancel(err)) {
        return reject(err.message);
      }
      console.error(err);
      reject(err);
    });
  });
};

const getDrugAlcoholReportData = (clientId, forUser, sites, reportType, signal = null) => {
  return new Promise((resolve, reject) => {
    let url = `/v1/clients/${clientId}/drugAlcohol?forUser=${forUser}&reportType=${reportType}`;
    if (reportType === 'daLocation') {
      url += `&selectedClientId=${sites}`
    } else {
      url += `&selectedDOTIds=${sites.join(',')}`;
    }
    axios.get(url, { signal })
    .then(response => {
      const results = response.data.results || [];
      resolve(results);
    })
    .catch(err => {
      if (axios.isCancel(err)) {
        return reject(err.message);
      }
      console.error(err);
      reject(err);
    });
  });
};

// TODO: remove this
const addRandomPool = (clientId, pools, topLevel, includeChildren, configuring = false, signal = null) => {
  return new Promise((resolve, reject) => {
    if (!clientId) {
      return reject(new Error('The client id is required'));
    }
    if (!pools || Object.keys(pools).length === 0) {
      return reject(new Error('The random pool is required'));
    }
    axios.post(`/v1/clients/${clientId}/pools?topLevel=${topLevel}&includeChildren=${includeChildren}&configuring=${configuring}`, pools, { signal })
    .then(res => {
      if (res.status !== 201 && res.status !== 200) {
        return reject(new Error('Unexpected response from server when adding new randoms pool to client'));
      }
      resolve(res.data);
    })
    .catch(err => {
      console.error(err);
      reject(err);
    });
  });
};

const addEZComplianceRandomsPool = (clientId, signal = null) => {
  return new Promise((resolve, reject) => {
    if (!clientId) {
      return reject(new Error('The client id is required'));
    }
    axios.post(`/v1/clients/${clientId}/pools?ezCompliance=true&topLevel=true`, {}, {signal})
    .then(res => {
      if (res.status !== 201) {
        return reject(new Error('Unexpected response from server when adding new randoms pool to client'));
      }
      resolve(res.data);
    })
    .catch(err => {
      console.error(err);
      reject(err);
    });
  });
};

const removeEZComplianceRandomsPool = (clientId, poolId, signal = null) => {
  return new Promise((resolve, reject) => {
    if (!poolId || !clientId) {
      return reject(new Error('pool id and client id is required'));
    }
    axios.delete(`/v1/clients/${clientId}/pools/${poolId}?ezCompliance=true&topLevel=true`, {signal})
    .then(res => {
      if (res.status !== 204) {
        return reject(new Error('Unexpected response from server when deleting provided randoms pool'));
      }
      resolve();
    })
    .catch(err => {
      console.error(err);
      reject(err);
    });
  });
};

const updateRandomPool = (pool, signal = null) => {
  return new Promise((resolve, reject) => {
    if (!pool || !pool._id) {
      return reject(new Error('pool is required'));
    }
    axios.patch(`/v1/clients/0/pools/${pool._id}`, pool, {signal})
    .then(res => {
      if (res.status !== 204) {
        return reject(new Error('Unexpected response from server when updating provided randoms pool'));
      }
      resolve();
    })
    .catch(err => {
      console.error(err);
      reject(err);
    });
  });
};

const deleteRandomPool = (clientId, poolId, topLevel, includeChildren = false, signal = null) => {
  return new Promise((resolve, reject) => {
    if (!poolId || !clientId) {
      return reject(new Error('pool id and client id is required'));
    }
    axios.delete(`/v1/clients/${clientId}/pools/${poolId}?topLevel=${topLevel}&includeChildren=${includeChildren}`, { signal })
    .then(res => {
      if (res.status !== 204) {
        return reject(new Error('Unexpected response from server when deleting provided randoms pool'));
      }
      resolve();
    })
    .catch(err => {
      console.error(err);
      reject(err);
    });
  });
};

const createRandomsPool = (clientId, poolDefinition, signal = null) => {
  return new Promise((resolve, reject) => {
    if (!clientId) {
      return reject(new Error('The client id is required'));
    }
    if (!poolDefinition) {
      return reject(new Error('The pool definition is required'));
    }
    axios.post(`/v1/clients/${clientId}/pools`, poolDefinition, {signal})
    .then(res => {
      if (res.status !== 204) {
        return reject(new Error('Unexpected response from server when creating randoms pool'));
      }
      resolve(res.data);
    })
    .catch(err => {
      console.error(err);
      reject(err);
    });
  });
};

const modifyRandomsPool = (clientId, poolDefinition, signal = null) => {
  return new Promise((resolve, reject) => {
    if (!clientId) {
      return reject(new Error('The client id is required'));
    }
    if (!poolDefinition) {
      return reject(new Error('The pool definition is required'));
    }
    axios.patch(`/v1/clients/${clientId}/pools/${poolDefinition._id}`, poolDefinition, { signal })
    .then(res => {
      if (res.status !== 204) {
        return reject(new Error('Unexpected response from server when updating provided randoms pool'));
      }
      resolve();
    })
    .catch(err => {
      console.error(err);
      reject(err);
    });
  });
};

const getPools = (clientId, availablePools = false, signal = null) => {
  return new Promise((resolve, reject) => {
    axios.get(`/v1/clients/${clientId}/pools?availablePools=${availablePools}`, { signal })
    .then(res => {
      if (res.status !== 200) {
        return reject(new Error('Unexpected response from server'));
      }
      resolve(res.data);
    })
    .catch(err => {
      if (axios.isCancel(err)) {
        return reject(err.message);
      }
      console.error(err);
      reject(err);
    });
  });
};

const getEmployeesForPool = (pool, clientId, sites, signal = null) => {
  return new Promise((resolve, reject) => {
    axios.post(`/v1/clients/${clientId}/pools/employeeList?sites=${sites}`, pool, { signal })
    .then(res => {
      if (res.status !== 201) {
        return reject(new Error('Unexpected response from server'));
      }
      resolve(res.data);
    })
    .catch(err => {
      console.error(err);
      reject(err);
    });
  });
};

const getLocationsForPool = (pool, clientId, signal = null) => {
  return new Promise((resolve, reject) => {
    axios.post(`/v1/clients/${clientId}/pools/locationList`, pool, {signal})
    .then(res => {
      if (res.status !== 201) {
        return reject(new Error('Unexpected response from server'));
      }
      resolve(res.data);
    })
    .catch(err => {
      console.error(err);
      reject(err);
    });
  });
};

const getUsersForClient = (clientId, forPermission = null, signal = null) => {
  return new Promise((resolve, reject) => {
    if (!clientId) {
      return reject(new Error('Invalid parameters. Unable to make request.'));
    }
    let url = `/v1/clients/${clientId}/users`;
    if (forPermission) {
      url += `?forPermission=${forPermission}`
    }
    axios.get(url, { signal })
    .then(res => {
      if (res.status !== 200) {
        return reject(new Error('Unexpected error from server'));
      }
      resolve(res.data);
    })
    .catch(err => {
      if (axios.isCancel(err)) {
        return reject(err.message);
      }
      console.error(err);
      reject(err);
    });
  });
};

const getJobTitleProgramsForClient = (legacyClientId, signal = null) => {
  return new Promise((resolve, reject) => {
    axios.get(`/v1/clients/${legacyClientId}/jobTitles?includePrograms=true&legacyClientId=${legacyClientId}`, { signal })
    .then(response => {
      resolve(response);
    })
    .catch(err => {
      if (axios.isCancel(err)) {
        return reject(err.message);
      }
      console.error(err);
      reject(err);
    });
  });
};

const addJobTitleToClient = (selectedJobTitle, legacyClientId, newJobTitleName, signal = null) => {
  return new Promise((resolve, reject) => {
    if (!legacyClientId) {
      return reject(new Error('The client id is required'));
    }
    if (!newJobTitleName && !selectedJobTitle) {
      return reject(new Error('The job title is required to be specified'));
    }
    let jobTitleId = -1;
    let jobTitleName = newJobTitleName;
    if (selectedJobTitle) {
      jobTitleId = selectedJobTitle.value;
      jobTitleName = selectedJobTitle.name;
    }
    axios.post(`/v1/clients/${legacyClientId}/jobTitles/${jobTitleId}?jobTitle=${jobTitleName}${newJobTitleName ? '&isNew=true' : ''}`, {}, { signal })
    .then(res => {
      if (res.status !== 201 && res.status !== 200) {
        return reject(new Error('Unexpected response from server when adding new job title to client'));
      }
      resolve(res.data);
    })
    .catch(err => {
      console.error(err);
      reject(err);
    });
  });
};

const updateClientHierarchy = (clientId, legacyClientId, LCodes, signal = null) => {
  return new Promise((resolve, reject) => {
    axios.patch(`/v1/clients/${clientId}/hierarchy`, {legacyClientId, LCodes}, { signal })
    .then(res => {
      resolve();
    })
    .catch(err => {
      console.error(err);
      reject(err);
    });
  });
};

const calculateRandomSelection = (clientId, poolId, signal = null) => {
  return new Promise((resolve, reject) => {
    axios.get(`/v1/clients/${clientId}/pools/${poolId}/selections/calculation`, { signal })
    .then(res => {
      resolve(res.data);
    })
    .catch(err => {
      console.error(err);
      reject(err);
    });
  });
};

const makeRandomSelection = (clientId, poolId, numToSelectDrug, numToSelectAlcohol, signal = null) => {
  return new Promise((resolve, reject) => {
    axios.post(`/v1/clients/${clientId}/pools/${poolId}/selections?drug=${numToSelectDrug}&alcohol=${numToSelectAlcohol}`, {}, { signal })
    .then(res => {
      resolve(res.data);
    })
    .catch(err => {
      console.error(err);
      reject(err);
    });
  });
};

const extendRandomSelection = (clientId, poolId, selectionId, numToSelectDrug, numToSelectAlcohol, signal = null) => {
  return new Promise((resolve, reject) => {
    axios.patch(`/v1/clients/${clientId}/pools/${poolId}/selections/${selectionId}`, {numToSelectDrug, numToSelectAlcohol}, { signal })
    .then(res => {
      resolve(res.data);
    })
    .catch(err => {
      console.error(err);
      reject(err);
    });
  });
};

const makeRandomAlternateSelection = (clientId, poolId, alternateValue, signal = null) => {
  return new Promise((resolve, reject) => {
    axios.post(`/v1/clients/${clientId}/pools/${poolId}/selections${!!alternateValue ? '?alternateNumber=' + alternateValue : ''}`, {}, { signal })
    .then(res => {
      resolve(res.data);
    })
    .catch(err => {
      console.error(err);
      reject(err);
    });
  });
};

const getPreviousRandomsPoolSelections = (clientId, signal = null) => {
  return new Promise((resolve, reject) => {
    axios.get(`/v1/clients/${clientId}/pools/selections/previous`, { signal })
    .then(result => {
      resolve(result.data)
    })
    .catch(err => {
      console.error(err);
      reject(err);
    });
  });
};


// getAlternates indicates to get the alternate list instead of the selection
// getSnapshot indicates to get the whole roster snapshot not just the selection
const getRandomSelection = ({clientId, poolId, selectionId, getAlternates = false, getSnapshot = false, isInternalUser, impersonatingUserId, signal = null}) => {
  return new Promise((resolve, reject) => {
    const optionString = `${'?'}${getAlternates ? 'alternates&' : ''}${getSnapshot ? 'snapshot&' : ''}${isInternalUser ? 'isInternalUser=true&' : ''}${impersonatingUserId ? `impersonatingUserId=${impersonatingUserId}` : ''}`;
    axios.get(`/v1/clients/${clientId}/pools/${poolId}/selections/${selectionId}${optionString}`, {signal})
    .then(result => {
      resolve(result.data);
    })
    .catch(err => {
      if (axios.isCancel(err)) {
        return reject(err.message);
      }
      console.error(err);
      reject(err);
    });
  });
};

const getDocTypesForClient = (clientId, signal = null) => {
  return new Promise((resolve, reject) => {
    axios.get(`/v1/clients/${clientId}/docTypes`, {signal})
    .then(response => {
      resolve(response);
    })
    .catch(err => {
      if (axios.isCancel(err)) {
        return reject(err.message);
      }
      console.error(err);
      reject(err);
    });
  });
};

const searchForDrugTestMatch = (clientId, searchData, signal = null) => {
  return new Promise((resolve, reject) => {
    axios.post(`/v1/clients/${clientId}/drugTestMatch`, {searchData}, { signal })
    .then(response => {
      resolve(response.data);
    })
    .catch(err => {
      if (axios.isCancel(err)) {
        return reject(err.message);
      }
      console.error(err);
      reject(err);
    });
  });
};

const saveDrugTestMatch = (clientId, employeeId, specimenReferenceId, signal = null) => {
  return new Promise((resolve, reject) => {
    axios.post(`/v1/clients/${clientId}/drugTestMatch/${specimenReferenceId}?employeeId=${employeeId}`, {}, { signal })
    .then(response => {
      resolve();
    })
    .catch(err => {
      if (axios.isCancel(err)) {
        return reject(err.message);
      }
      console.error(err);
      reject(err);
    });
  });
};

const updateDrugTestMatch = (clientId, employeeId, specimenReferenceId, data, signal = null) => {
  return new Promise((resolve, reject) => {
    axios.patch(`/v1/clients/${clientId}/drugTestMatch/${specimenReferenceId}?employeeId=${employeeId}`, data, { signal })
    .then(response => {
      resolve();
    })
    .catch(err => {
      if (axios.isCancel(err)) {
        return reject(err.message);
      }
      console.error(err);
      reject(err);
    });
  });
};


const getSelectionsForEmployee = (clientId, employeeId, signal = null) => {
  return new Promise((resolve, reject) => {
    axios.get(`/v1/clients/${clientId}/selections/${employeeId}`, { signal })
    .then(response => {
      resolve(response.data);
    })
    .catch(err => {
      if (axios.isCancel(err)) {
        return reject(err.message);
      }
      console.error(err);
      reject(err);
    });
  });
};

const emailRandomSelection = (clientId, poolId, selectionId, emailList, signal = null) => {
  return new Promise((resolve, reject) => {
    axios.post(`/v1/clients/${clientId}/pools/${poolId}/selections/${selectionId}/email`, {emailList}, { signal })
    .then(result => resolve(result.data))
    .catch(err => {
      if (axios.isCancel(err)) {
        return reject(err.message);
      }
      console.error(err);
      reject(err);
    });
  });
};

const copySettingsFromLocation = (clientId, clientFrom, fields, signal = null) => {
  return new Promise((resolve, reject) => {
    axios.patch(`/v1/clients/${clientId}?settingsFrom=${clientFrom}&fields=${fields.join()}&legacy=true`, {}, { signal })
    .then(result => resolve())
    .catch(err => {
      if (axios.isCancel(err)) {
        return reject(err.message);
      }
      console.error(err);
      reject(err);
    });
  });
};

const getEmployeeCount = (clientId, jobTitleId, signal = null) => {
  return new Promise((resolve, reject) => {
    axios.get(`/v1/clients/${clientId}/employees/count?jobTitleId=${jobTitleId}`, { signal })
    .then(response => {
      resolve(response.data);
    })
    .catch(err => {
      if (axios.isCancel(err)) {
        return reject(err.message);
      }
      console.error(err);
      reject(err);
    });
  });
};

const deleteJobTitleTransferEmployees = (clientId, deleteJobTitleId, transferJobTitle, signal = null) => {
  return new Promise((resolve, reject) => {
    axios.patch(`/v1/clients/${clientId}/removeJobTitle?deleteJobTitleId=${deleteJobTitleId}&transferJobTitle=${transferJobTitle}`, { signal })
    .then(() => resolve())
    .catch(err => {
      if (axios.isCancel(err)) {
        return reject(err.message);
      }
      console.error(err);
      reject(err);
    });
  });
};

const getClientMVRScoreDefs = (clientId, signal = null) => {
  return new Promise((resolve, reject) => {
    axios.get(`/v1/clients/${clientId}/MVRScoreDefs`, {signal})
    .then(response => {
      resolve(response.data);
    })
    .catch(err => {
      if (axios.isCancel(err)) {
        return reject(err.message);
      }
      console.error(err);
      reject(err);
    });
  });
};

const getClientMVRScoringRules = (clientId, signal = null) => {
  return new Promise((resolve, reject) => {
    axios.get(`/v1/clients/${clientId}/MVRScoringRules`, {signal})
    .then(response => {
      resolve(response.data);
    })
    .catch(err => {
      if (axios.isCancel(err)) {
        return reject(err.message);
      }
      console.error(err);
      reject(err);
    });
  });
};

const getClientMVRScoringReasons = (clientId, signal = null) => {
  return new Promise((resolve, reject) => {
    axios.get(`/v1/clients/${clientId}/MVRScoringReasons`, {signal})
    .then(response => {
      resolve(response.data);
    })
    .catch(err => {
      if (axios.isCancel(err)) {
        return reject(err.message);
      }
      console.error(err);
      reject(err);
    });
  });
};

const searchByDOTNumber = (DOTNumber, signal = null) => {
  return new Promise((resolve, reject) => {
    axios.get(`/v1/clients?source=legacy&dotnumber=${DOTNumber}`, {signal})
    .then(response => {
      resolve(response.data);
    })
    .catch(err => {
      if (axios.isCancel(err)) {
        return reject(err.message);
      }
      console.error(err);
      reject(err);
    });
  });
};

const getClientLocations = (clientId, signal = null) => {
  return new Promise((resolve, reject) => {
    axios.get(`/v1/clients?descendantsOf=${clientId}&filters=active&source=legacy&aliases=true&flatten=true`, {signal})
    .then(response => resolve(response.data))
    .catch(err => {
      if (axios.isCancel(err)) {
        return reject(err.message);
      }
      console.error(err);
      reject(err);
    });
  });
};

const updateLocationAliases = (integrationTypeId, aliases, signal = null) => {
  return new Promise((resolve, reject) => {
    axios.patch(`/v1/clients/aliases/${integrationTypeId}`, aliases, {signal})
    .then(response => {
      resolve();
    })
    .catch(err => {
      if (axios.isCancel(err)) {
        return reject(err.message);
      }
      console.error(err);
      reject(err);
    });
  });
};

const getClearinghouseQueryAlerts = (clientId, sites, problemsAndWarningsOnly, signal) => {
  return new Promise((resolve, reject) => {
    axios.get(`/v1/clients/${clientId}/alerts?alertTypes=clearinghouseQueries&sites=${sites}&problemsAndWarningsOnly=${problemsAndWarningsOnly}`, { signal })
    .then(response => {
      resolve(response.data.clearinghouseQueryAlerts);
    })
    .catch(err => {
      if (axios.isCancel(err)) {
        return reject(err.message);
      }
      console.error(err);
      reject(err);
    });
  });
};

// TODO - move this to Concorde API
const getAllJobTitlesForAClient = async (clientMongoId) => {
  const { data } = await axios.get(`/v1/clients/${clientMongoId}/jobTitles?forCompleteHierarchy=true`)
  return data;
}

export default {
  getAllJobTitlesForAClient,
  getClientList,
  getClient,
  getClientByLegacyId,
  getClientsOverview,
  getTopLevelClient,
  updateClient,
  setNotes,
  setServices,
  uploadLogo,
  getNonImportedLegacyClientList,
  getClientSummary,
  getClientComplianceSummary,
  getClientAudits,
  getActiveEmployeesBySite,
  getAlerts,
  getDocReviewAlerts,
  getStaleApplicantAlerts,
  getCSAAlerts,
  getDACAlerts,
  getDocumentAlerts,
  getMissingDocuments,
  getMissingAndExpiredDocuments,
  getCSAReportData,
  getCrashReportData,
  getReportOfAllMedCardsByLocation,
  getAccidentReportData,
  getDrugAlcoholReportData,
  addRandomPool,  // TODO: remove this
  getEmployeesForPool,
  getLocationsForPool,
  updateRandomPool,
  deleteRandomPool,
  createRandomsPool,
  modifyRandomsPool,
  addEZComplianceRandomsPool,
  removeEZComplianceRandomsPool,
  patchClient,
  getPools,
  getUsersForClient,
  getJobTitleProgramsForClient,
  addJobTitleToClient,
  getEmployeesDOTReportData,
  getFastTestResultsForClient,
  getRandomsPoolsForClient,
  getRandomsPoolsByClient,
  updateClientHierarchy,
  calculateRandomSelection,
  makeRandomSelection,
  extendRandomSelection,
  makeRandomAlternateSelection,
  getPreviousRandomsPoolSelections,
  getRandomSelection,
  getDocTypesForClient,
  searchForDrugTestMatch,
  saveDrugTestMatch,
  updateDrugTestMatch,
  getSelectionsForEmployee,
  emailRandomSelection,
  copySettingsFromLocation,
  getEmployeeCount,
  deleteJobTitleTransferEmployees,
  getClientMVRScoreDefs,
  getClientMVRScoringRules,
  getClientMVRScoringReasons,
  searchByDOTNumber,
  getClientLocations,
  updateLocationAliases,
  getClearinghouseQueryAlerts
};